import { z } from "zod";
import { addressSchema, envelope, uint } from "../../../api/launchpad";
import { launchContracts, type LaunchCall } from "../../../contracts/launchpad";
import { zeroAddress, type Address, type Hex } from "viem";

const uint256 = uint.refine(value => /^\d+$/.test(value) && BigInt(value) < 2n ** 256n);
const prepared = z.object({
  kind: z.string().min(1), to: addressSchema, value: uint256,
  data: z.custom<Hex>(value => typeof value === "string" && /^0x[0-9a-fA-F]{8}(?:[0-9a-fA-F]{2})*$/.test(value)),
  token: addressSchema.optional(), symbol: z.string().optional(), currency: addressSchema.optional(),
  pairSymbol: z.string().optional(), positionTokenId: uint256.optional(),
});
const claimAll = prepared.extend({
  kind: z.literal("claim-all-fees"),
  positionTokenIds: z.array(uint256),
  currencies: z.array(addressSchema),
}).refine(tx => tx.to.toLowerCase() === launchContracts.locker.toLowerCase());
const balance = z.object({ raw: uint256, formatted: z.string().regex(/^\d+(\.\d+)?$/) });
const asset = balance.extend({ address: addressSchema, symbol: z.string().min(1) });
const antiClaimAll = prepared.extend({ kind: z.literal("claim-all-anti-sniper-fees"), currencies: z.array(addressSchema) })
  .refine(tx => tx.to.toLowerCase() === launchContracts.sniperHook.toLowerCase());
const antiSniper = z.object({ configured: z.boolean(), split: z.object({ creatorPct: z.literal(50), protocolPct: z.literal(50) }),
    totalClaimable: z.object({ HYPE: balance, tokens: z.array(asset) }), claimAllTransaction: antiClaimAll.nullable() });
const feeDetails = z.object({

  split: z.object({ creatorPct: z.literal(80), protocolPct: z.literal(20) }),
  liquidity: z.object({ HYPE: balance, KNTQ: balance, NVDAx: balance, memeTokens: z.array(asset) }),
  totalClaimable: z.object({ simulated: z.boolean(), HYPE: balance, tokens: z.array(asset) }).nullish(),
  collectTransactions: z.array(prepared), claimTransactions: z.array(prepared), collectRequiredBeforeClaim: z.boolean(),
  claimAllTransaction: claimAll.nullable(),
});
const identity = z.object({ account: addressSchema, recipient: addressSchema, antiSniper: antiSniper.optional() });
const feeResponse = envelope(z.discriminatedUnion("configured", [
  identity.extend({ configured: z.literal(false) }),
  identity.merge(feeDetails).extend({ configured: z.literal(true) }),
]));
export type FeeRole = "creator" | "protocol";
type RawFees = Extract<z.infer<typeof feeResponse>["data"], { configured: true }>;
export type ConfiguredFees = Pick<RawFees, "configured" | "account" | "recipient" | "totalClaimable"> & {
  split: { creatorPct: 80 | 50; protocolPct: 20 | 50 };
  claimAllTransaction: z.infer<typeof claimAll> | z.infer<typeof antiClaimAll> | null;
};
export const creatorFees = (account: Address, role: FeeRole = "creator", kind: "lp" | "anti" = "lp") => feeResponse.refine(({ data }) =>
  data.recipient.toLowerCase() === account.toLowerCase() &&
  (role === "protocol" ? !data.configured || data.account !== zeroAddress : data.account.toLowerCase() === account.toLowerCase())).transform(response => {
    if (kind === "lp") return response;
    const anti = response.data.antiSniper;
    if (!anti?.configured) return { ...response, data: { configured: false as const, account: response.data.account, recipient: response.data.recipient } };
    return { ...response, data: { ...response.data, configured: true as const, split: anti.split,
      totalClaimable: { ...anti.totalClaimable, simulated: true }, claimAllTransaction: anti.claimAllTransaction } };
  });

export function feeClaimCall(data: ConfiguredFees, account: Address): LaunchCall {
  const tx = data.claimAllTransaction;
  if (data.account.toLowerCase() !== account.toLowerCase() || data.recipient.toLowerCase() !== account.toLowerCase() || !tx) {
    throw new Error("No claim-all transaction is available for this wallet and recipient.");
  }
  return { to: tx.to, data: tx.data, value: BigInt(tx.value), verifyMinedCall: true };
}

export function feeReviewKey(data: ConfiguredFees) {
  return JSON.stringify([data.account.toLowerCase(), data.recipient.toLowerCase(), data.totalClaimable, data.claimAllTransaction]);
}
